import { Role as RoleItem } from '@seek/profile-read-ui';
import { useTranslations } from '@vocab/react';
import { Actions, Button, Notice, Stack, Text } from 'braid-design-system';
import { v4 as uuid } from 'uuid';

import type { Role } from 'src/__generated__/graphql';
import { useConfig } from 'src/context/config';
import { logError } from 'src/modules/error-logger/browserLog';
import { useEditorDrawer } from 'src/ui/EditorDrawer/EditorDrawer';

import translations from './.vocab';
import { useCareerHistoryMutations } from './hooks/useCareerHistoryMutations';
import { useTracking } from './hooks/useTracking';

export function CareerHistoryList({
  unconfirmedRoles,
  roles,
  hideWhenEditing = false,
}: {
  unconfirmedRoles: Role[];
  roles: Role[];
  hideWhenEditing?: boolean;
}) {
  const { config } = useConfig();
  const { t } = useTranslations(translations);
  const { onEditorOpen, editingId } = useEditorDrawer();

  const { trackAddRole, trackRejectRole } = useTracking();
  const { confirmRole, rejectRole } = useCareerHistoryMutations();

  const handleConfirm = async (id: string) => {
    const role = unconfirmedRoles.find((r) => r.id === id);
    if (!role) {
      return;
    }
    try {
      await confirmRole(role);

      trackAddRole(role, 'cv parsed');
    } catch (err: any) {
      // handle err
      logError({ ...err, type: 'confirm-role' }, { error: err });
    }
  };

  const handleReject = async (id: string) => {
    const role = unconfirmedRoles.find((r) => r.id === id);
    if (!role) {
      return;
    }
    try {
      await rejectRole(id);

      trackRejectRole();
    } catch (err: any) {
      // handle err
      logError({ ...err, type: 'reject-role' }, { error: err });
    }
  };

  if (hideWhenEditing && editingId) {
    return null;
  }

  const allRoles = [...unconfirmedRoles, ...roles];

  return (
    <Stack space="large">
      <Stack space="large">
        {unconfirmedRoles.length > 0 && (
          <Notice tone="promote">
            <Text>
              {t(`We found these roles in your resumé. Add them to your`, {
                brand: config.BRAND,
              })}
            </Text>
          </Notice>
        )}
        <Stack component="ul" space="small">
          {allRoles.map((role) => {
            const isConfirmed = roles.includes(role);
            return (
              <li key={`${role.id}-${isConfirmed}`}>
                <RoleItem
                  id={role.id}
                  displayVariant="card"
                  confirmed={isConfirmed}
                  title={role.title}
                  company={role.company}
                  achievements={role.achievements}
                  from={role.from}
                  to={role.to}
                  editRole={onEditorOpen}
                  addUnconfirmedRole={handleConfirm}
                  rejectUnconfirmedRole={handleReject}
                />
              </li>
            );
          })}
        </Stack>
      </Stack>
      <Actions>
        <Button
          tone="formAccent"
          variant="ghost"
          onClick={() => onEditorOpen(uuid())}
          data={{ automation: 'add-role' }}
        >
          {t('Add role')}
        </Button>
      </Actions>
    </Stack>
  );
}
